import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, ChevronDown, X } from 'lucide-react';

/**
 * Single-select dropdown with a search box. Replaces a native <select> where
 * the list can grow past a handful of rows — geofences, POIs and forms all
 * reach hundreds on a real fleet.
 *
 * `options` are `{ id, label, sublabel? }`. onChange receives the whole option
 * (callers need more than the id) or null when cleared.
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder,
  searchPlaceholder,
  emptyLabel,
  clearable = true,
  clearLabel,
  loading = false,
  unknownLabel,
  disabled = false,
  className = '',
  // Replaces the trigger's own look. A toolbar that already has native
  // <select>s next to it needs the same height and shade, or the one
  // SearchableSelect in the row reads as a different kind of control.
  buttonClassName = '',
  // false hides the search box: a three-item list (Group by) has nothing to search.
  searchable = true,
  ariaLabel,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open && searchable) inputRef.current?.focus();
    else setQuery('');
  }, [open]);

  const selected = useMemo(
    // '' is a legitimate option id ("All groups"), so it is looked up like any other value.
    () => (value != null ? options.find((o) => String(o.id) === String(value)) || null : null),
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? options.filter((o) => String(o.label || '').toLowerCase().includes(q)) : options;
    return list.slice(0, 100);
  }, [options, query]);

  useEffect(() => { setActive(0); }, [query, open]);

  const onSearchKey = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && filtered[active]) { e.preventDefault(); onChange(filtered[active]); setOpen(false); }
  };

  // A value the option list doesn't carry (deleted, or not loaded yet) must
  // still read as set — falling back to the placeholder would claim nothing
  // is selected when something is.
  const buttonLabel = selected
    ? selected.label
    : value != null && value !== ''
      ? (unknownLabel ? unknownLabel(value) : String(value))
      : (placeholder ?? t('common.select', 'Select…'));

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center gap-1.5 text-start disabled:opacity-50 ${
          buttonClassName || 'rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 px-3 py-2 text-sm'
        }`}
      >
        <span className={`flex-1 truncate ${selected ? 'text-slate-700 dark:text-slate-200' : 'text-slate-400'}`}>
          <bdi>{buttonLabel}</bdi>
        </span>
        {clearable && value != null && value !== '' && (
          <span
            role="button"
            tabIndex={-1}
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            title={clearLabel || t('common.clear', 'Clear')}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
          >
            <X size={14} />
          </span>
        )}
        <ChevronDown size={14} className="text-slate-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl">
          {searchable && (
          <div className="relative border-b border-slate-100 dark:border-slate-700">
            <Search size={13} className="absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" style={{ insetInlineStart: 10 }} />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onSearchKey}
              placeholder={searchPlaceholder ?? t('common.search', 'Search…')}
              className="w-full bg-transparent ps-8 pe-2 py-2 text-sm text-slate-700 dark:text-slate-200 outline-none"
            />
          </div>
          )}
          <div className="max-h-60 overflow-y-auto">
            {clearable && clearLabel && (
              <button
                type="button"
                onClick={() => { onChange(null); setOpen(false); }}
                className="w-full text-start px-3 py-1.5 text-sm text-slate-500 dark:text-slate-400 hover:bg-sky-50 dark:hover:bg-sky-500/10"
              >
                {clearLabel}
              </button>
            )}
            {loading && (
              <div className="px-3 py-2 text-xs text-slate-400">{t('common.loading', 'Loading…')}</div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="px-3 py-2 text-xs text-slate-400">
                {emptyLabel ?? t('common.noMatches', 'No matches')}
              </div>
            )}
            {filtered.map((o, idx) => (
              <button
                key={o.id}
                type="button"
                data-option-id={o.id}
                aria-current={idx === active && searchable ? 'true' : undefined}
                onClick={() => { onChange(o); setOpen(false); }}
                className={`w-full text-start px-3 py-1.5 text-sm hover:bg-sky-50 dark:hover:bg-sky-500/10 ${
                  idx === active && searchable ? 'bg-sky-50 dark:bg-sky-500/10 ' : ''}${
                  String(o.id) === String(value)
                    ? 'text-sky-600 dark:text-sky-400 font-medium'
                    : 'text-slate-700 dark:text-slate-200'
                }`}
              >
                <bdi>{o.label}</bdi>
                {o.sublabel && (
                  <span className="ms-1.5 text-[10px] text-slate-400">{o.sublabel}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
